/**
 * CompactHistoryGrid Component
 *
 * Spreadsheet-like grid view for the medical history log.
 * - Diagnosis rows with linked medications as sub-rows
 * - Unlinked medications table
 * - Procedures table
 * - Click-to-edit cells, sortable columns
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import {
  Check,
  Bot,
  Edit2,
  Trash2,
  Eye,
  Plus,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Strikethrough,
  Undo2,
  FileText,
} from 'lucide-react';
import type {
  HistoryDiagnosis,
  HistoryMedication,
  HistoryProcedure,
  MedDxLink,
  ConfidenceLevel,
  SourcePage,
} from '../../types';
import type { HistoryEntry, HistoryEntryType } from './HistoryEntryCard';

interface CompactHistoryGridProps {
  diagnoses: HistoryDiagnosis[];
  medications: HistoryMedication[];
  procedures: HistoryProcedure[];
  medDxLinks: MedDxLink[];
  onEditDiagnosis?: (id: string, field: string, value: any) => void;
  onEditMedication?: (id: string, field: string, value: any) => void;
  onEditProcedure?: (id: string, field: string, value: any) => void;
  onVerifyEntry?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
  onDeleteEntry?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
  onAddEntry?: (entryType: HistoryEntryType) => void;
  onStrikeOut?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
  onUndoStrikeOut?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
  onPageClick?: (documentId: string, page: number) => void;
}

interface DiagnosisRow {
  diagnosis: HistoryDiagnosis;
  linkedMedications: HistoryMedication[];
}

type SortField = 'name' | 'status' | 'date';
type SortDir = 'asc' | 'desc';

const CONFIDENCE_COLORS: Record<ConfidenceLevel, string> = {
  high: 'bg-green-500',
  medium: 'bg-amber-400',
  low: 'bg-red-400',
};

const STATUS_DOT_COLORS: Record<string, string> = {
  active: 'bg-green-500',
  resolved: 'bg-gray-400',
  historical: 'bg-blue-400',
  uncertain: 'bg-amber-400',
  current: 'bg-green-500',
  discontinued: 'bg-gray-400',
  prn: 'bg-amber-400',
  completed: 'bg-green-500',
  planned: 'bg-blue-400',
  cancelled: 'bg-gray-400',
};

const STATUS_ORDER: Record<string, number> = {
  active: 0, current: 0, completed: 0,
  uncertain: 1, prn: 1, planned: 1,
  historical: 2,
  resolved: 3, discontinued: 3, cancelled: 3,
};

function EditableCell({
  value,
  onSave,
  type = 'text',
  options,
  className = '',
}: {
  value: string;
  onSave: (val: string) => void;
  type?: 'text' | 'select';
  options?: Array<{ value: string; label: string }>;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [editing]);

  // Resync the edit buffer with external value changes (optimistic update /
  // refetch) while NOT editing, so a later inline edit starts from current data
  // and handleSave can't push a stale value back to the server. Never clobbers
  // in-progress typing (guarded on !editing).
  useEffect(() => {
    if (!editing) setEditValue(value);
  }, [value, editing]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setEditValue(value);
      setEditing(false);
    }
  };

  // Select type: always render a native <select> styled as plain text so one click opens the dropdown
  if (type === 'select' && options) {
    return (
      <select
        value={value}
        onChange={e => onSave(e.target.value)}
        className={`text-xs bg-transparent border-none cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded px-1 py-0.5 -mx-1 w-full focus:outline-none focus:ring-1 focus:ring-primary-400 appearance-none ${className}`}
        title="Click to change"
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }

  if (editing) {
    return (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="text-xs border border-primary-300 dark:border-primary-700 rounded px-1 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
      />
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      className={`cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded px-1 py-0.5 -mx-1 min-h-[1.5em] w-full ${className}`}
      title="Click to edit"
    >
      {value || <span className="text-gray-300 dark:text-gray-600">--</span>}
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT_COLORS[status] || 'bg-gray-300 dark:bg-gray-600'}`} />
      <span className="capitalize">{status}</span>
    </span>
  );
}

function ActionButtons({
  entry,
  entryType,
  onVerify,
  onDelete,
  onStrikeOut,
  onUndoStrikeOut,
}: {
  entry: HistoryEntry;
  entryType: HistoryEntryType;
  onVerify?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
  onDelete?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
  onStrikeOut?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
  onUndoStrikeOut?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
}) {
  const isStruckOut = !!entry.struck_out;
  return (
    <div className="flex items-center gap-0.5">
      {!isStruckOut && onVerify && !entry.verified_by && (
        <button
          onClick={() => onVerify(entry, entryType)}
          className="p-1 text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 rounded"
          title="Verify"
        >
          <Eye size={12} />
        </button>
      )}
      {!isStruckOut && onStrikeOut && !entry.is_merged_duplicate && (
        <button
          onClick={() => onStrikeOut(entry, entryType)}
          className="p-1 text-gray-400 dark:text-gray-500 hover:text-amber-600 dark:hover:text-amber-400 rounded"
          title="Strike out"
        >
          <Strikethrough size={12} />
        </button>
      )}
      {isStruckOut && onUndoStrikeOut && (
        <button
          onClick={() => onUndoStrikeOut(entry, entryType)}
          className="p-1 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 rounded"
          title="Undo strike out"
        >
          <Undo2 size={12} />
        </button>
      )}
      {onDelete && (
        <button
          onClick={() => onDelete(entry, entryType)}
          className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded"
          title="Delete"
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

function PageLinks({
  sourcePages,
  onPageClick,
}: {
  sourcePages: SourcePage[];
  onPageClick?: (documentId: string, page: number) => void;
}) {
  if (!sourcePages || sourcePages.length === 0 || !onPageClick) return null;
  // Group by document, show compact page numbers
  const groups = new Map<string, number[]>();
  for (const sp of sourcePages) {
    const pages = groups.get(sp.document_id) || [];
    if (!pages.includes(sp.page)) pages.push(sp.page);
    groups.set(sp.document_id, pages);
  }
  for (const [docId, pages] of groups) {
    groups.set(docId, pages.sort((a, b) => a - b));
  }

  return (
    <span className="inline-flex items-center gap-0.5 text-[10px]">
      {Array.from(groups.entries()).map(([docId, pages]) => (
        <span key={docId} className="inline-flex items-center gap-0.5">
          <FileText size={9} className="text-gray-400 dark:text-gray-500" />
          {pages.map((p, i) => (
            <span key={p}>
              {i > 0 && ','}
              <button
                onClick={() => onPageClick(docId, p)}
                className="text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline"
              >
                {p}
              </button>
            </span>
          ))}
        </span>
      ))}
    </span>
  );
}

function EntryBadges({ entry }: { entry: HistoryEntry }) {
  return (
    <>
      {entry.verified_by && (
        <Check size={10} className="text-green-500 dark:text-green-400 inline ml-0.5" />
      )}
      {entry.is_ai_generated && (
        <span className="inline-flex items-center ml-1 px-1 py-0 text-[9px] font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 rounded">
          AI
        </span>
      )}
    </>
  );
}

export function CompactHistoryGrid({
  diagnoses,
  medications,
  procedures,
  medDxLinks,
  onEditDiagnosis,
  onEditMedication,
  onEditProcedure,
  onVerifyEntry,
  onDeleteEntry,
  onAddEntry,
  onStrikeOut,
  onUndoStrikeOut,
  onPageClick,
}: CompactHistoryGridProps) {
  const [dxSort, setDxSort] = useState<{ field: SortField; dir: SortDir }>({ field: 'status', dir: 'asc' });
  const [procSort, setProcSort] = useState<{ field: SortField; dir: SortDir }>({ field: 'status', dir: 'asc' });
  const [collapsedDx, setCollapsedDx] = useState<Set<string>>(new Set());

  // Filter merged duplicates
  const activeDx = useMemo(() => diagnoses.filter(d => !d.is_merged_duplicate), [diagnoses]);
  const activeMeds = useMemo(() => medications.filter(m => !m.is_merged_duplicate), [medications]);
  const activeProcs = useMemo(() => procedures.filter(p => !p.is_merged_duplicate), [procedures]);

  // Build diagnosis rows with linked meds
  const diagnosisRows: DiagnosisRow[] = useMemo(() => {
    const linkedMedIds = new Set<string>();
    const rows = activeDx.map(dx => {
      // Find meds linked via medDxLinks
      const linkMedIds = medDxLinks
        .filter(l => l.diagnosis_id === dx.id && !l.rejected)
        .map(l => l.medication_id);
      // Also find meds that have this dx in linked_diagnosis_ids
      const directLinked = activeMeds.filter(
        m => m.linked_diagnosis_ids?.includes(dx.id) && !linkMedIds.includes(m.id)
      );
      const allLinkedMeds = [
        ...activeMeds.filter(m => linkMedIds.includes(m.id)),
        ...directLinked,
      ];
      allLinkedMeds.forEach(m => linkedMedIds.add(m.id));
      return { diagnosis: dx, linkedMedications: allLinkedMeds };
    });

    // Sort
    rows.sort((a, b) => {
      const mult = dxSort.dir === 'asc' ? 1 : -1;
      if (dxSort.field === 'status') {
        const sa = STATUS_ORDER[a.diagnosis.status] ?? 99;
        const sb = STATUS_ORDER[b.diagnosis.status] ?? 99;
        if (sa !== sb) return (sa - sb) * mult;
        return a.diagnosis.condition_name.localeCompare(b.diagnosis.condition_name) * mult;
      }
      if (dxSort.field === 'name') {
        return a.diagnosis.condition_name.localeCompare(b.diagnosis.condition_name) * mult;
      }
      // date
      const da = a.diagnosis.onset_date || '';
      const db = b.diagnosis.onset_date || '';
      return da.localeCompare(db) * mult;
    });

    return rows;
  }, [activeDx, activeMeds, medDxLinks, dxSort]);

  // Unlinked medications
  const unlinkedMeds = useMemo(() => {
    const linkedMedIds = new Set<string>();
    diagnosisRows.forEach(r => r.linkedMedications.forEach(m => linkedMedIds.add(m.id)));
    return activeMeds.filter(m => !linkedMedIds.has(m.id));
  }, [activeMeds, diagnosisRows]);

  // Sorted procedures
  const sortedProcs = useMemo(() => {
    const sorted = [...activeProcs];
    sorted.sort((a, b) => {
      const mult = procSort.dir === 'asc' ? 1 : -1;
      if (procSort.field === 'status') {
        const sa = STATUS_ORDER[a.status] ?? 99;
        const sb = STATUS_ORDER[b.status] ?? 99;
        if (sa !== sb) return (sa - sb) * mult;
        return a.procedure_name.localeCompare(b.procedure_name) * mult;
      }
      if (procSort.field === 'name') {
        return a.procedure_name.localeCompare(b.procedure_name) * mult;
      }
      const da = a.procedure_date || '';
      const db = b.procedure_date || '';
      return da.localeCompare(db) * mult;
    });
    return sorted;
  }, [activeProcs, procSort]);

  const toggleDxSort = (field: SortField) => {
    setDxSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleProcSort = (field: SortField) => {
    setProcSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleCollapsed = (dxId: string) => {
    setCollapsedDx(prev => {
      const next = new Set(prev);
      if (next.has(dxId)) next.delete(dxId);
      else next.add(dxId);
      return next;
    });
  };

  const DX_STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'historical', label: 'Historical' },
    { value: 'uncertain', label: 'Uncertain' },
  ];

  const MED_STATUS_OPTIONS = [
    { value: 'current', label: 'Current' },
    { value: 'discontinued', label: 'Discontinued' },
    { value: 'historical', label: 'Historical' },
    { value: 'prn', label: 'PRN' },
    { value: 'uncertain', label: 'Uncertain' },
  ];

  const PROC_STATUS_OPTIONS = [
    { value: 'completed', label: 'Completed' },
    { value: 'planned', label: 'Planned' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'uncertain', label: 'Uncertain' },
  ];

  const SortHeader = ({ label, field, sortState, onSort }: {
    label: string;
    field: SortField;
    sortState: { field: SortField; dir: SortDir };
    onSort: (f: SortField) => void;
  }) => (
    <th
      className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 select-none"
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        {sortState.field === field && (
          <ArrowUpDown size={10} className="text-primary-500 dark:text-primary-400" />
        )}
      </span>
    </th>
  );

  return (
    <div className="space-y-6">
      {/* Diagnoses + Linked Medications Grid */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Diagnoses & Linked Medications
            <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">({activeDx.length})</span>
          </h3>
          {onAddEntry && (
            <button
              onClick={() => onAddEntry('diagnosis')}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
            >
              <Plus size={12} />
              Add Diagnosis
            </button>
          )}
        </div>

        {activeDx.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500 py-4 text-center">No diagnoses</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="w-full table-auto border-collapse text-xs">
              <thead className="sticky top-0 bg-white dark:bg-gray-800 z-10">
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="w-[3px] p-0" />
                  <th className="w-6 px-1 py-1.5" />
                  <SortHeader label="Diagnosis" field="name" sortState={dxSort} onSort={toggleDxSort} />
                  <SortHeader label="Status" field="status" sortState={dxSort} onSort={toggleDxSort} />
                  <SortHeader label="Onset" field="date" sortState={dxSort} onSort={toggleDxSort} />
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-l border-gray-100 dark:border-gray-700">Medication</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dose</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Freq</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Route</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Med Status</th>
                  <th className="px-2 py-1.5 w-16" />
                </tr>
              </thead>
              <tbody>
                {diagnosisRows.map((row, rowIdx) => {
                  const dx = row.diagnosis;
                  const meds = row.linkedMedications;
                  const isCollapsed = collapsedDx.has(dx.id);
                  const medCount = meds.length;
                  const rowSpan = isCollapsed ? 1 : Math.max(1, medCount);

                  return meds.length === 0 || isCollapsed ? (
                    // Single row: no meds or collapsed
                    <tr
                      key={dx.id}
                      className={`group/row border-b border-gray-100 dark:border-gray-700 ${rowIdx % 2 === 1 ? 'bg-gray-50 dark:bg-gray-900/40' : ''} hover:bg-primary-50/30 dark:hover:bg-primary-900/20`}
                    >
                      <td className={`w-[3px] p-0 ${CONFIDENCE_COLORS[dx.confidence] || CONFIDENCE_COLORS.medium}`} />
                      <td className="px-1 py-1.5 text-center">
                        {medCount > 0 && (
                          <button onClick={() => toggleCollapsed(dx.id)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                            {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                          </button>
                        )}
                      </td>
                      <td className={`px-2 py-1.5 font-medium max-w-[200px] ${dx.struck_out ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                        {onEditDiagnosis && !dx.struck_out ? (
                          <EditableCell
                            value={dx.condition_name}
                            onSave={val => onEditDiagnosis(dx.id, 'condition_name', val)}
                          />
                        ) : dx.condition_name}
                        <EntryBadges entry={dx} />
                        {isCollapsed && medCount > 0 && (
                          <span className="ml-1 text-[10px] text-gray-400 dark:text-gray-500">({medCount} meds)</span>
                        )}
                        <PageLinks sourcePages={dx.source_pages} onPageClick={onPageClick} />
                      </td>
                      <td className="px-2 py-1.5">
                        {onEditDiagnosis && !dx.struck_out ? (
                          <EditableCell
                            value={dx.status}
                            type="select"
                            options={DX_STATUS_OPTIONS}
                            onSave={val => onEditDiagnosis(dx.id, 'status', val)}
                          />
                        ) : <StatusDot status={dx.status} />}
                      </td>
                      <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">
                        {onEditDiagnosis && !dx.struck_out ? (
                          <EditableCell value={dx.onset_date || ''} onSave={val => onEditDiagnosis(dx.id, 'onset_date', val)} />
                        ) : dx.onset_date || '--'}
                      </td>
                      <td className="px-2 py-1.5 border-l border-gray-100 dark:border-gray-700 text-gray-300 dark:text-gray-600" colSpan={5}>
                        {isCollapsed ? `${medCount} linked` : '--'}
                      </td>
                      <td className="px-2 py-1.5">
                        <ActionButtons entry={dx} entryType="diagnosis" onVerify={onVerifyEntry} onDelete={onDeleteEntry} onStrikeOut={onStrikeOut} onUndoStrikeOut={onUndoStrikeOut} />
                      </td>
                    </tr>
                  ) : (
                    // Multiple rows: first row has dx + first med, subsequent rows have just meds
                    meds.map((med, medIdx) => (
                      <tr
                        key={`${dx.id}-${med.id}`}
                        className={`group/row border-b border-gray-100 dark:border-gray-700 ${rowIdx % 2 === 1 ? 'bg-gray-50 dark:bg-gray-900/40' : ''} hover:bg-primary-50/30 dark:hover:bg-primary-900/20`}
                      >
                        {medIdx === 0 && (
                          <>
                            <td className={`w-[3px] p-0 ${CONFIDENCE_COLORS[dx.confidence] || CONFIDENCE_COLORS.medium}`} rowSpan={rowSpan} />
                            <td className="px-1 py-1.5 text-center" rowSpan={rowSpan}>
                              <button onClick={() => toggleCollapsed(dx.id)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                                <ChevronDown size={12} />
                              </button>
                            </td>
                            <td className={`px-2 py-1.5 font-medium max-w-[200px] ${dx.struck_out ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`} rowSpan={rowSpan}>
                              {onEditDiagnosis && !dx.struck_out ? (
                                <EditableCell
                                  value={dx.condition_name}
                                  onSave={val => onEditDiagnosis(dx.id, 'condition_name', val)}
                                />
                              ) : dx.condition_name}
                              <EntryBadges entry={dx} />
                              <PageLinks sourcePages={dx.source_pages} onPageClick={onPageClick} />
                            </td>
                            <td className="px-2 py-1.5" rowSpan={rowSpan}>
                              {onEditDiagnosis && !dx.struck_out ? (
                                <EditableCell
                                  value={dx.status}
                                  type="select"
                                  options={DX_STATUS_OPTIONS}
                                  onSave={val => onEditDiagnosis(dx.id, 'status', val)}
                                />
                              ) : <StatusDot status={dx.status} />}
                            </td>
                            <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400" rowSpan={rowSpan}>
                              {onEditDiagnosis && !dx.struck_out ? (
                                <EditableCell value={dx.onset_date || ''} onSave={val => onEditDiagnosis(dx.id, 'onset_date', val)} />
                              ) : dx.onset_date || '--'}
                            </td>
                          </>
                        )}
                        <td className={`px-2 py-1.5 border-l border-gray-100 dark:border-gray-700 font-medium ${med.struck_out ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-200'}`}>
                          {onEditMedication && !med.struck_out ? (
                            <EditableCell
                              value={med.medication_name}
                              onSave={val => onEditMedication(med.id, 'medication_name', val)}
                            />
                          ) : med.medication_name}
                          <EntryBadges entry={med} />
                          <PageLinks sourcePages={med.source_pages} onPageClick={onPageClick} />
                        </td>
                        <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">
                          {onEditMedication && !med.struck_out ? (
                            <EditableCell value={med.dose || ''} onSave={val => onEditMedication(med.id, 'dose', val)} />
                          ) : med.dose || '--'}
                        </td>
                        <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">
                          {onEditMedication && !med.struck_out ? (
                            <EditableCell value={med.frequency || ''} onSave={val => onEditMedication(med.id, 'frequency', val)} />
                          ) : med.frequency || '--'}
                        </td>
                        <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">
                          {onEditMedication && !med.struck_out ? (
                            <EditableCell value={med.route || ''} onSave={val => onEditMedication(med.id, 'route', val)} />
                          ) : med.route || '--'}
                        </td>
                        <td className="px-2 py-1.5">
                          {onEditMedication && !med.struck_out ? (
                            <EditableCell
                              value={med.status}
                              type="select"
                              options={MED_STATUS_OPTIONS}
                              onSave={val => onEditMedication(med.id, 'status', val)}
                            />
                          ) : <StatusDot status={med.status} />}
                        </td>
                        <td className="px-2 py-1.5">
                          {medIdx === 0 ? (
                            <ActionButtons entry={dx} entryType="diagnosis" onVerify={onVerifyEntry} onDelete={onDeleteEntry} onStrikeOut={onStrikeOut} onUndoStrikeOut={onUndoStrikeOut} />
                          ) : (
                            <ActionButtons entry={med} entryType="medication" onVerify={onVerifyEntry} onDelete={onDeleteEntry} onStrikeOut={onStrikeOut} onUndoStrikeOut={onUndoStrikeOut} />
                          )}
                        </td>
                      </tr>
                    ))
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Unlinked Medications */}
      {unlinkedMeds.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Unlinked Medications
              <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">({unlinkedMeds.length})</span>
            </h3>
            {onAddEntry && (
              <button
                onClick={() => onAddEntry('medication')}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
              >
                <Plus size={12} />
                Add Medication
              </button>
            )}
          </div>
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="w-full table-auto border-collapse text-xs">
              <thead className="sticky top-0 bg-white dark:bg-gray-800 z-10">
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="w-[3px] p-0" />
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Medication</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dose</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Freq</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Route</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Start</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">End</th>
                  <th className="px-2 py-1.5 w-16" />
                </tr>
              </thead>
              <tbody>
                {unlinkedMeds.map((med, idx) => (
                  <tr
                    key={med.id}
                    className={`group/row border-b border-gray-100 dark:border-gray-700 ${idx % 2 === 1 ? 'bg-gray-50 dark:bg-gray-900/40' : ''} hover:bg-primary-50/30 dark:hover:bg-primary-900/20`}
                  >
                    <td className={`w-[3px] p-0 ${CONFIDENCE_COLORS[med.confidence] || CONFIDENCE_COLORS.medium}`} />
                    <td className={`px-2 py-1.5 font-medium ${med.struck_out ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                      {onEditMedication && !med.struck_out ? (
                        <EditableCell value={med.medication_name} onSave={val => onEditMedication(med.id, 'medication_name', val)} />
                      ) : med.medication_name}
                      <EntryBadges entry={med} />
                      <PageLinks sourcePages={med.source_pages} onPageClick={onPageClick} />
                    </td>
                    <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">
                      {onEditMedication && !med.struck_out ? (
                        <EditableCell value={med.dose || ''} onSave={val => onEditMedication(med.id, 'dose', val)} />
                      ) : med.dose || '--'}
                    </td>
                    <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">
                      {onEditMedication && !med.struck_out ? (
                        <EditableCell value={med.frequency || ''} onSave={val => onEditMedication(med.id, 'frequency', val)} />
                      ) : med.frequency || '--'}
                    </td>
                    <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">
                      {onEditMedication && !med.struck_out ? (
                        <EditableCell value={med.route || ''} onSave={val => onEditMedication(med.id, 'route', val)} />
                      ) : med.route || '--'}
                    </td>
                    <td className="px-2 py-1.5">
                      {onEditMedication && !med.struck_out ? (
                        <EditableCell
                          value={med.status}
                          type="select"
                          options={MED_STATUS_OPTIONS}
                          onSave={val => onEditMedication(med.id, 'status', val)}
                        />
                      ) : <StatusDot status={med.status} />}
                    </td>
                    <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">
                      {onEditMedication && !med.struck_out ? (
                        <EditableCell value={med.start_date || ''} onSave={val => onEditMedication(med.id, 'start_date', val)} />
                      ) : med.start_date || '--'}
                    </td>
                    <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">
                      {onEditMedication && !med.struck_out ? (
                        <EditableCell value={med.end_date || ''} onSave={val => onEditMedication(med.id, 'end_date', val)} />
                      ) : med.end_date || '--'}
                    </td>
                    <td className="px-2 py-1.5">
                      <ActionButtons entry={med} entryType="medication" onVerify={onVerifyEntry} onDelete={onDeleteEntry} onStrikeOut={onStrikeOut} onUndoStrikeOut={onUndoStrikeOut} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Procedures */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Procedures
            <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">({activeProcs.length})</span>
          </h3>
          {onAddEntry && (
            <button
              onClick={() => onAddEntry('procedure')}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
            >
              <Plus size={12} />
              Add Procedure
            </button>
          )}
        </div>

        {activeProcs.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500 py-4 text-center">No procedures</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="w-full table-auto border-collapse text-xs">
              <thead className="sticky top-0 bg-white dark:bg-gray-800 z-10">
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="w-[3px] p-0" />
                  <SortHeader label="Procedure" field="name" sortState={procSort} onSort={toggleProcSort} />
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">CPT</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                  <SortHeader label="Date" field="date" sortState={procSort} onSort={toggleProcSort} />
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Outcome</th>
                  <SortHeader label="Status" field="status" sortState={procSort} onSort={toggleProcSort} />
                  <th className="px-2 py-1.5 w-16" />
                </tr>
              </thead>
              <tbody>
                {sortedProcs.map((proc, idx) => (
                  <tr
                    key={proc.id}
                    className={`group/row border-b border-gray-100 dark:border-gray-700 ${idx % 2 === 1 ? 'bg-gray-50 dark:bg-gray-900/40' : ''} hover:bg-primary-50/30 dark:hover:bg-primary-900/20`}
                  >
                    <td className={`w-[3px] p-0 ${CONFIDENCE_COLORS[proc.confidence] || CONFIDENCE_COLORS.medium}`} />
                    <td className={`px-2 py-1.5 font-medium ${proc.struck_out ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                      {onEditProcedure && !proc.struck_out ? (
                        <EditableCell value={proc.procedure_name} onSave={val => onEditProcedure(proc.id, 'procedure_name', val)} />
                      ) : proc.procedure_name}
                      <EntryBadges entry={proc} />
                      <PageLinks sourcePages={proc.source_pages} onPageClick={onPageClick} />
                    </td>
                    <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">{proc.cpt_codes?.join(', ') || '--'}</td>
                    <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400 capitalize">{proc.procedure_type || '--'}</td>
                    <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">
                      {onEditProcedure && !proc.struck_out ? (
                        <EditableCell value={proc.procedure_date || ''} onSave={val => onEditProcedure(proc.id, 'procedure_date', val)} />
                      ) : proc.procedure_date || '--'}
                    </td>
                    <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400">{proc.outcome || '--'}</td>
                    <td className="px-2 py-1.5">
                      {onEditProcedure && !proc.struck_out ? (
                        <EditableCell
                          value={proc.status}
                          type="select"
                          options={PROC_STATUS_OPTIONS}
                          onSave={val => onEditProcedure(proc.id, 'status', val)}
                        />
                      ) : <StatusDot status={proc.status} />}
                    </td>
                    <td className="px-2 py-1.5">
                      <ActionButtons entry={proc} entryType="procedure" onVerify={onVerifyEntry} onDelete={onDeleteEntry} onStrikeOut={onStrikeOut} onUndoStrikeOut={onUndoStrikeOut} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
